import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Calendar, Clock, FileText, CheckCircle, XCircle, Loader2, Download, Trash2 } from 'lucide-react';
import { ExecutionStatus, DownloadHistory } from './types';

interface AlertsSectionProps {
  status: ExecutionStatus;
  singleDate: string;
  onDateChange: (date: string) => void;
  onExecute: () => void;
  alertHistory: DownloadHistory[];
  onRedownload: (item: DownloadHistory) => Promise<void>;
  onDeleteHistory?: (item: DownloadHistory) => Promise<void>;
  actionsDisabled?: boolean;
  tokenCost?: number;
  tokenBalance?: number | null;
}

const AlertsSection: React.FC<AlertsSectionProps> = ({
  status,
  singleDate,
  onDateChange,
  onExecute,
  alertHistory,
  onRedownload,
  onDeleteHistory,
  actionsDisabled,
  tokenCost = 1,
  tokenBalance = null
}) => {
  const hasTokens = tokenBalance === null ? true : tokenBalance >= tokenCost;
  const executeDisabled = status.status === 'running' || !!actionsDisabled || !hasTokens;

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Left Column - Alerts Configuration */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
      >
        {/* Card Header */}
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#0084CF] flex items-center justify-center">
              <AlertTriangle size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-black text-gray-950 tracking-tight" style={{ color: '#16202B', fontWeight: 900 }}>Generate Alerts</h3>
              <p className="text-xs font-black text-gray-900 tracking-tight" style={{ color: '#16202B', fontWeight: 900 }}>Select date and download alerts file</p>
            </div>
          </div>
        </div>

        {/* Card Content */}
        <div className="p-4 space-y-4">
          {/* Date Selector */}
          <div>
            <label className="block text-sm font-extrabold text-gray-950 mb-2" style={{ color: '#16202B', fontWeight: 900 }}>
              Select Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={singleDate}
                onChange={(e) => onDateChange(e.target.value)}
                placeholder="mm/dd/yyyy"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg font-semibold focus:ring-2 focus:ring-[#0084CF] focus:border-transparent transition-all placeholder:text-gray-400 placeholder:font-semibold"
                style={{ color: '#16202B', fontWeight: 600 }}
              />

            </div>
          </div>

          {/* Status Indicator - Inline */}
          {status.status !== 'idle' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg ${status.status === 'running'
                ? 'bg-green-50 border border-green-100'
                : status.status === 'completed'
                  ? 'bg-green-50 border border-green-100'
                  : 'bg-red-50 border border-red-100'
                }`}
            >
              {status.status === 'running' && (
                <Loader2 size={18} className="text-[#0084CF] animate-spin" />
              )}
              {status.status === 'completed' && (
                <CheckCircle size={18} className="text-[#1E7F4F]" />
              )}
              {status.status === 'failed' && (
                <XCircle size={18} className="text-[#B42318]" />
              )}
              <div className="flex-1">
                <p className={`text-sm font-extrabold ${status.status === 'running' ? 'text-[#0084CF]' :
                  status.status === 'completed' ? 'text-[#1E7F4F]' :
                    'text-[#B42318]'
                  }`}>
                  {status.status === 'running' ? 'Processing...' :
                    status.status === 'completed' ? 'Download Completed' :
                      'Download Failed'}
                </p>
                {status.message && (
                  <p className="text-xs text-gray-900 mt-0.5 font-extrabold" style={{ color: '#16202B', fontWeight: 900 }}>{status.message}</p>
                )}
              </div>
            </motion.div>
          )}

          {/* Download Button */}
          <button
            onClick={onExecute}
            disabled={executeDisabled}
            className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-sm transition-all ${executeDisabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-[#0084CF] text-white hover:bg-[#0066A3] shadow-sm hover:shadow-md'
              }`}
            title={actionsDisabled ? 'Only HO Admin can execute simulation tabs.' : !hasTokens ? `Insufficient tokens. Required: ${tokenCost}, Available: ${tokenBalance ?? 0}` : 'Generate Alerts'}
          >
            {status.status === 'running' ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Processing Download...</span>
              </>
            ) : (
              <>
                <AlertTriangle size={18} />
                <span className="font-extrabold">GENERATE ALERTS</span>
                <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-white/20">
                  {tokenBalance === null ? 'UNLIMITED TOKENS' : `${tokenBalance ?? 0}/${tokenCost} TOKENS`}
                </span>
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Right Column - Alerts History */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
      >
        {/* History Header */}
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <Clock size={20} className="text-gray-600" />
            </div>
            <div>
              <h3 className="text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 900 }}>Download History</h3>
              <p className="text-xs text-gray-900 font-extrabold" style={{ color: '#16202B', fontWeight: 900 }}>Recent CSV downloads and their status</p>
            </div>
          </div>
        </div>

        {/* History Table */}
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto relative bg-white">
          <table className="w-full border-collapse">
            <thead className="bg-[#F2F8FC] border-b-2 border-gray-200">
              <tr style={{ height: '48px' }}>
                <th className="px-4 py-3 text-left text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  File Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  Size
                </th>
                <th className="px-4 py-3 text-left text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-black text-black uppercase tracking-wider" style={{ color: '#16202B', fontWeight: 900, backgroundColor: '#F2F8FC' }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {alertHistory.length > 0 ? (
                alertHistory.map((item) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-[#0084CF] flex-shrink-0" />
                        <span className="text-xs text-gray-950 font-extrabold truncate" title={item.filename} style={{ color: '#16202B', fontWeight: 900 }}>
                          {item.filename}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <Calendar size={12} className="text-gray-400" />
                        <span className="text-xs text-gray-950 font-extrabold" style={{ color: '#16202B', fontWeight: 900 }}>{item.dateDisplay}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-950 font-extrabold" style={{ color: '#16202B', fontWeight: 900 }}>
                      {item.size}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${item.status === 'completed'
                        ? 'bg-green-50 text-[#1E7F4F]'
                        : 'bg-red-50 text-[#B42318]'
                        }`}>
                        {item.status === 'completed' ? (
                          <CheckCircle size={10} />
                        ) : (
                          <XCircle size={10} />
                        )}
                        {item.status === 'completed' ? 'Done' : 'Failed'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => onRedownload(item)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#0084CF] hover:bg-[#0066A3] text-white transition-colors"
                          title="Redownload"
                        >
                          <Download size={14} />
                        </button>
                        <button
                          onClick={() => onDeleteHistory && onDeleteHistory(item)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 transition-colors border border-red-200"
                          title="Delete History"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <FileText size={32} className="text-gray-300" />
                      <p className="text-sm text-gray-950 font-extrabold" style={{ color: '#16202B', fontWeight: 900 }}>No download history available</p>
                      <p className="text-xs text-gray-900 font-extrabold" style={{ color: '#16202B', fontWeight: 900 }}>CSV files will appear here after download</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default AlertsSection;
